'use client';
import React, { useRef, useState } from 'react';
import Image from 'next/image';
import { motion, useInView, useReducedMotion } from 'motion/react';
import { cn } from 'lib/utils';
import { type BloomProps, Bloom, HeaderSection, Icon, Wrapper } from 'ui';

export type VideoProps = {
  className?: string;
  eyebrow?: string;
  title?: string;
  intro?: string;
  videoUrl: string;
  thumbnail?: {
    src: string;
    alt: string;
    width: number;
    height: number;
  };
  tracks?: {
    src: string;
    srcLang: string;
    label: string;
    kind?: 'captions' | 'subtitles' | 'descriptions' | 'chapters' | 'metadata';
    default?: boolean;
  }[];
  bloom?: BloomProps;
};

const Video = ({
  className,
  eyebrow,
  title,
  intro,
  videoUrl,
  thumbnail,
  tracks,
  bloom,
}: VideoProps) => {
  const ref = useRef(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-20%' });
  const prefersReducedMotion = useReducedMotion();
  const [isPlaying, setIsPlaying] = useState(false);

  const showThumbnail = !!thumbnail && !isPlaying;

  const handlePlay = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setIsPlaying(true);
    videoRef.current?.play();
  };

  const headerInitial = prefersReducedMotion
    ? { opacity: 0 }
    : { opacity: 0, y: 30 };
  const headerAnimate = isInView
    ? prefersReducedMotion
      ? { opacity: 1 }
      : { opacity: 1, y: 0 }
    : headerInitial;

  return (
    <motion.section {...{ ref }}>
      <Wrapper className={cn('relative flex flex-col gap-14', className)}>
        <Bloom {...bloom} />

        <motion.div
          initial={headerInitial}
          animate={headerAnimate}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <HeaderSection
            className="relative z-2"
            content={intro}
            alignment="center"
            eyebrowAsTitle={!!eyebrow}
            {...{
              eyebrow,
              title,
            }}
          />
        </motion.div>

        <div
          className="relative z-2 w-full max-w-300 mx-auto"
          style={
            thumbnail
              ? { aspectRatio: `${thumbnail.width} / ${thumbnail.height}` }
              : undefined
          }
        >
          <video
            ref={videoRef}
            className={cn(
              'w-full h-auto block',
              thumbnail && 'absolute inset-0 h-full object-cover'
            )}
            src={videoUrl}
            controls={!showThumbnail}
            playsInline
            preload="metadata"
            aria-hidden={showThumbnail || undefined}
            tabIndex={showThumbnail ? -1 : undefined}
          >
            {tracks?.map((track) => (
              <track
                key={`${track.srcLang}-${track.kind ?? 'captions'}`}
                src={track.src}
                srcLang={track.srcLang}
                label={track.label}
                kind={track.kind ?? 'captions'}
                default={track.default}
              />
            ))}
          </video>

          {showThumbnail && (
            <button
              type="button"
              className="group absolute inset-0 cursor-pointer overflow-hidden focus-visible:outline-2 focus-visible:outline-offset-5 focus-visible:outline-athletic-500"
              onClick={handlePlay}
              aria-label={`Play video${title ? `: ${title}` : ''}`}
            >
              <Image
                src={thumbnail.src}
                alt=""
                width={thumbnail.width}
                height={thumbnail.height}
                className="w-full h-full object-cover"
              />
              <span
                aria-hidden
                className="absolute inset-0 flex items-center justify-center bg-graphite-950/20 transition-colors group-hover:bg-graphite-950/30"
              >
                <Icon
                  name="play"
                  className="h-23 w-auto fill-clay-50 transition-transform group-hover:scale-110"
                  aria-hidden
                />
              </span>
            </button>
          )}
        </div>
      </Wrapper>
    </motion.section>
  );
};

export default Video;
